import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../../components/AdminLayout";
import { Cabecalho, Pagina } from "../../../components/AdminLayout/Pagina";
import ListaPaginada from "./ListaPaginada";
import Exclusao from "./Exclusao";
import Migalhas from "./Migalhas";
import { Aviso, Corpo } from "./styled";
import { caminhoEditar, caminhoNovo, RECURSOS } from "./recursos";

const Acoes = ({ editar, onExcluir, nome, verbo = "Excluir", alternar }) => (
  <div className="acoes">
    <Link to={editar} aria-label={`Editar ${nome}`}>Editar</Link>
    {alternar &&
      <button type="button" className={alternar.ativo ? "" : "habilitar"} disabled={alternar.ocupado}
        aria-label={`${alternar.ativo ? "Desabilitar" : "Habilitar"} ${nome}`} onClick={alternar.onClick}>
        {alternar.ativo ? "Desabilitar" : "Habilitar"}
      </button>
    }
    <button type="button" className="perigo" aria-label={`${verbo} ${nome}`} onClick={onExcluir}>{verbo}</button>
  </div>
);

const Ligacao = ({ href, vazio = "—" }) => (href
  ? <a className="link" href={href} target="_blank" rel="noopener noreferrer">{href}</a>
  : <span className="meta">{vazio}</span>);

// De onde veio o apoiador: pedido feito pelo app (nome, estado e foto vêm do perfil
// da pessoa) ou cadastro manual do administrador.
const Origem = ({ a }) => (a.origem === "app"
  ? <span className="selo origem-app" title="Veio de um pedido feito pelo app: nome, estado e foto são os do perfil da pessoa">Do app</span>
  : <span className="selo origem-manual" title="Cadastrado à mão pelo administrador">Cadastro manual</span>);

// Colunas de cada cadastro. `ac(item)` devolve o botão de ações da linha.
const COLUNAS = {
  biomas: (ac) => [
    { id: "nome", rotulo: "Bioma", largura: "minmax(220px, 4fr)", celula: (b) => <span className="nome">{b.nome}</span> },
    { id: "acoes", rotulo: "Ações", largura: "minmax(170px, 1fr)", direita: true, celula: (b) => ac(b, b.nome) },
  ],
  especies: (ac) => [
    { id: "comum", rotulo: "Nome comum", largura: "minmax(180px, 2fr)", celula: (e) => <span className="nome">{e.nome_comum || "—"}</span> },
    { id: "cientifico", rotulo: "Nome científico", largura: "minmax(200px, 2.4fr)", celula: (e) => <span className="cientifico">{e.nome_cientifico}</span> },
    { id: "biomas", rotulo: "Biomas", largura: "minmax(160px, 2fr)",
      celula: (e) => (e.biomas?.length
        ? <div className="chips">{e.biomas.map((b) => <span key={b.id} className="chip">{b.nome}</span>)}</div>
        : <span className="meta">—</span>) },
    { id: "acoes", rotulo: "Ações", largura: "minmax(170px, auto)", direita: true, celula: (e) => ac(e, e.nome_comum || e.nome_cientifico) },
  ],
  banners: (ac) => [
    { id: "imagem", rotulo: "Banner", largura: "120px",
      celula: (p) => (p.foto?.path
        ? <img className="miniatura" src={p.foto.path} alt={`Banner de ${p.nome}`} loading="lazy" />
        : <span className="sem-imagem">sem imagem</span>) },
    { id: "nome", rotulo: "Patrocinador", largura: "minmax(160px, 2fr)", celula: (p) => <span className="nome">{p.nome}</span> },
    { id: "link", rotulo: "Link", largura: "minmax(180px, 3fr)", celula: (p) => <Ligacao href={p.link} /> },
    { id: "status", rotulo: "Status", largura: "110px", celula: (p) => <span className={`selo ${p.status}`}>{p.status === "Inativo" ? "Desabilitado" : p.status}</span> },
    { id: "acoes", rotulo: "Ações", largura: "minmax(170px, auto)", direita: true, celula: (p) => ac(p, p.nome) },
  ],
  apoiadores: (ac) => [
    { id: "foto", rotulo: "Foto", largura: "64px",
      celula: (a) => (a.foto?.path
        ? <img className="foto" src={a.foto.path} alt={`Foto de ${a.nome}`} loading="lazy" />
        : <span className="meta">—</span>) },
    // Estado junto do nome e link junto da declaracao: menos colunas, cabe em lista na tela comum.
    { id: "nome", rotulo: "Apoiador", largura: "minmax(150px, 1.4fr)",
      celula: (a) => <div><span className="nome">{a.nome}</span>{a.estado && <span className="meta" style={{ display: "block" }}>{a.estado}</span>}</div> },
    { id: "origem", rotulo: "Origem", largura: "100px", celula: (a) => <Origem a={a} /> },
    { id: "descricao", rotulo: "Declaração e link", largura: "minmax(180px, 3fr)",
      celula: (a) => <div><span className="meta">{a.descricao}</span><span style={{ display: "block", marginTop: 4 }}><Ligacao href={a.link} vazio="Sem link" /></span></div> },
    { id: "status", rotulo: "Status", largura: "100px", celula: (a) => <span className={`selo ${a.status}`}>{a.status === "Inativo" ? "Desabilitado" : a.status}</span> },
    { id: "acoes", rotulo: "Ações", largura: "minmax(150px, auto)", direita: true, celula: (a) => ac(a, a.nome, "Remover") },
  ],
};

// Largura (do próprio bloco) a partir da qual a lista vira colunas.
const LARGURA_MINIMA = { biomas: 520, especies: 780, banners: 820, apoiadores: 860 };

const EXCLUSAO = {
  biomas: (i) => ({ titulo: `Excluir o bioma ${i.nome}?`, subtitulo: "Certifique-se de que este bioma não está associado a nenhuma espécie." }),
  especies: (i) => ({ titulo: `Excluir a espécie ${i.nome_comum || i.nome_cientifico}?`, subtitulo: "A espécie some da lista de escolha do app. Plantios já registrados com ela impedem a exclusão." }),
  banners: (i) => ({ titulo: `Excluir o banner de ${i.nome}?`, subtitulo: "O banner deixa de aparecer no app. Esta ação não pode ser desfeita." }),
  // Apoiador: "remover" (a pessoa deixa de aparecer; o perfil dela não é tocado).
  apoiadores: (i) => ({
    titulo: `Remover o apoiador ${i.nome}?`,
    subtitulo: i.origem === "app"
      ? "A pessoa deixa de aparecer como apoiadora no app; o perfil dela não é afetado. Ela poderá pedir de novo pelo app."
      : "O apoiador deixa de aparecer no app. Esta ação não pode ser desfeita.",
  }),
};

/**
 * Página de um cadastro de conteúdo (/admin/conteudo/:recurso): a lista ocupa a
 * página toda, com busca, "Ver mais", adicionar, editar e excluir. Adicionar e
 * editar abrem PÁGINAS de formulário, não modais.
 */
export default function Lista() {
  const { recurso } = useParams();
  const cfg = RECURSOS[recurso];
  const navigate = useNavigate();
  const location = useLocation();

  const [aviso, setAviso] = useState(location.state?.aviso || null);
  const [versao, setVersao] = useState(0);
  const [exclusao, setExclusao] = useState(null);
  const [alternando, setAlternando] = useState(null);

  // O aviso que veio da página de formulário não pode reaparecer num F5.
  useEffect(() => {
    if (location.state?.aviso) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!cfg) return <Navigate to="/admin/conteudo" replace />;

  // Habilitar/desabilitar direto na linha (banners e apoiadores): o item some do site e do app
  // quando "Inativo" e volta quando "Ativo", sem apagar nem refazer o cadastro.
  async function alternarStatus(item, nome) {
    if (alternando) return;
    setAlternando(item.id);
    setAviso(null);
    const novo = item.status === "Inativo" ? "Ativo" : "Inativo";
    const r = await cfg.model.alterarStatus(item.id, novo);
    setAlternando(null);
    const status = r?.status ?? r?.response?.status;
    if (status >= 200 && status < 300) {
      setAviso({ texto: novo === "Ativo" ? `${nome} habilitado: voltou a aparecer no site e no app.` : `${nome} desabilitado: não aparece mais no site e no app.` });
      setVersao((v) => v + 1);
    } else {
      setAviso({ erro: true, texto: r?.response?.data?.message || "Não foi possível alterar agora. Tente novamente." });
    }
  }
  const acoes = (item, nome, verbo) => (
    <Acoes nome={nome} verbo={verbo} editar={caminhoEditar(recurso, item.id)} onExcluir={() => { setAviso(null); setExclusao(item); }}
      alternar={cfg.model?.alterarStatus ? { ativo: item.status !== "Inativo", ocupado: alternando === item.id, onClick: () => alternarStatus(item, nome) } : null} />
  );
  const colunas = COLUNAS[recurso](acoes);
  const textosExclusao = exclusao ? EXCLUSAO[recurso](exclusao) : null;

  return (
    <AdminLayout>
      <Pagina>
        <Migalhas itens={[{ rotulo: "Conteúdo", para: "/admin/conteudo" }, { rotulo: cfg.titulo }]} />
        <Cabecalho titulo={cfg.titulo} descricao={cfg.descricao} />

        {aviso &&
          <Aviso className={aviso.erro ? "erro" : undefined} role={aviso.erro ? "alert" : "status"}>
            <span>{aviso.texto}</span>
            <button type="button" aria-label="Fechar aviso" onClick={() => setAviso(null)}>×</button>
          </Aviso>
        }

        <Corpo>
          <ListaPaginada
            key={recurso}
            rotulo={cfg.titulo} listar={cfg.model.listar} colunas={colunas} chave={(i) => `${recurso}-${i.id}`} versao={versao}
            porPagina={cfg.porPagina || 10}
            textoBusca={cfg.busca.texto} placeholderBusca={cfg.busca.placeholder}
            rotuloNovo={cfg.adicionar} onNovo={() => navigate(caminhoNovo(recurso))}
            vazio={cfg.vazio} larguraMinima={LARGURA_MINIMA[recurso]}
          />
        </Corpo>

        {exclusao &&
          <Exclusao
            item={exclusao} excluir={cfg.model.delete} textoBotao={recurso === "apoiadores" ? "Remover" : "Excluir"}
            titulo={textosExclusao.titulo} subtitulo={textosExclusao.subtitulo}
            onCancelar={() => setExclusao(null)}
            onExcluido={() => { setExclusao(null); setAviso({ texto: cfg.excluido }); setVersao((v) => v + 1); }}
          />
        }
      </Pagina>
    </AdminLayout>
  );
}
